import { Components, Enums, Types } from '@sendum/core'
import React, { forwardRef, useCallback, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { toast } from 'react-hot-toast'
import Select, { StylesConfig } from 'react-select'

import s from './Pallet.module.scss'

export interface IAddExistingPhysicalPalletModalRef {
    open: (pallet: Types.Pallet) => void
    close: () => void
}

interface IProps {
    addToExistingPallet: (ppn: string, palletFilling: Enums.PalletFillingType, notes: string) => void
    getHalfFillingPhysicalPallets: (batchId: string, nodeName: string) => Promise<any[] | undefined>
}

export const AddExistingPhysicalPalletModal = forwardRef<IAddExistingPhysicalPalletModalRef, IProps>(
    ({ addToExistingPallet, getHalfFillingPhysicalPallets }, ref) => {
        const [isLoading, setLoading] = useState<boolean>(false)
        const [physicalNumberExistingPallet, setPhysicalNumberExistingPallet] = useState<string>('')
        const [palletFilling, setPalletFilling] = useState<Enums.PalletFillingType>()
        const [notes, setNotes] = useState<string>('')
        const [selectPhysicalNumberExistingPalletOptions, setSelectPhysicalNumberExistingPalletOptions] = useState<
            { value: string; label: string; filling: string }[]
        >([])
        const [selectPalletFillingOptions, setSelectPalletFillingOptions] = useState<
            { value: Enums.PalletFillingType; label: string }[]
        >([])
        const [selectPalletFillingValue, setSelectPalletFillingValue] = useState<{
            value: Enums.PalletFillingType
            label: string
        }>()

        const modalRef = useRef<Components.IModalRef>(null)

        const add = useCallback(() => {
            if (!physicalNumberExistingPallet || !palletFilling) {
                return
            }
            addToExistingPallet(physicalNumberExistingPallet, palletFilling, notes)
            modalRef.current?.close()
        }, [addToExistingPallet, physicalNumberExistingPallet, palletFilling, notes])

        const isDisabledAddButton = useMemo<boolean>(() => {
            return isLoading || !physicalNumberExistingPallet || !palletFilling
        }, [isLoading, physicalNumberExistingPallet, palletFilling])

        const actions = useMemo<Components.IAction[]>(
            () => [
                { title: 'Add', variant: 'primary', func: add, disabled: isDisabledAddButton },
                { title: 'Cancel', variant: 'cancel', func: () => modalRef.current?.close() }
            ],
            [add, isDisabledAddButton]
        )

        useImperativeHandle(ref, () => ({
            open(pallet: Types.Pallet) {
                if (!pallet?.Batch?.BatchId || !pallet?.Location?.LocationName) {
                    console.error('Incorrect data for the current pallet')
                    toast.error('Incorrect data for the current pallet')
                    return
                }
                setLoading(true)
                setPhysicalNumberExistingPallet('')
                setSelectPalletFillingValue(undefined)
                setNotes('')
                setSelectPhysicalNumberExistingPalletOptions([])
                setSelectPalletFillingOptions([])

                getHalfFillingPhysicalPallets(pallet?.Batch?.BatchId, pallet?.Location?.LocationName)
                    .then(halfFillingPhysicalPallets => {
                        if (halfFillingPhysicalPallets?.length) {
                            setSelectPhysicalNumberExistingPalletOptions(
                                halfFillingPhysicalPallets.map(halfFillingPhysicalPallet => {
                                    return {
                                        value: halfFillingPhysicalPallet.PhysicalPalletNumber as string,
                                        label: halfFillingPhysicalPallet.PhysicalPalletNumber as string,
                                        filling: halfFillingPhysicalPallet.Filling as string
                                    }
                                })
                            )
                        } else {
                            toast.error('No existing pallets to add')
                            // modalRef.current?.close()
                        }
                    })
                    .catch(e => {
                        console.error('Error getHalfFillingPhysicalPallets ', e)
                        toast.error('Error get half filling physical pallets')
                    })
                    .finally(() => setLoading(false))
                modalRef.current?.open()
            },
            close() {
                modalRef.current?.close()
            }
        }))

        const onChangePhysicalNumberExistingPallet = (event: any) => {
            setPhysicalNumberExistingPallet(event.value)
            const newOptions =
                event.filling === Enums.PalletFillingType.BOTTOM
                    ? [{ value: Enums.PalletFillingType.TOP, label: 'TOP' }]
                    : [{ value: Enums.PalletFillingType.BOTTOM, label: 'BOTTOM' }]

            setSelectPalletFillingOptions([...newOptions])
            setSelectPalletFillingValue({ ...newOptions[0] })
            setPalletFilling(newOptions[0].value)
        }

        const onChangePalletFilling = (event: any) => {
            setPalletFilling(event.value)
        }

        const colourStyles: StylesConfig<any, any> = {
            control: styles => ({ ...styles, ...{ fontSize: '14px' } }),
            input: styles => ({ ...styles, ...{ fontSize: '14px' } }),
            option: styles => ({ ...styles, ...{ fontSize: '14px' } }),
            placeholder: styles => ({ ...styles, ...{ fontSize: '14px' } }),
            singleValue: styles => ({ ...styles, ...{ fontSize: '14px' } })
        }

        return (
            <Components.Modal
                ref={modalRef}
                title="Add existing pallet"
                actions={actions}
                width={420}
                testId="add-existing-physical-pallet-modal"
            >
                <div className={`${s.Pallet} ${s.Pallet__inModal}`}>
                    {isLoading && <Components.Loader className={s.Pallet__loader} />}
                    <div className={s.Pallet__column}>
                        <div className={s.Pallet__fullItem}>
                            <span>
                                Please select an <br />
                                existing pallet
                            </span>
                            <Select
                                styles={colourStyles}
                                options={selectPhysicalNumberExistingPalletOptions}
                                onChange={onChangePhysicalNumberExistingPallet}
                            />
                        </div>
                        <div className={s.Pallet__fullItem}>
                            <span>
                                Select which part of the <br />
                                pallet will be filled
                            </span>
                            <Select
                                styles={colourStyles}
                                value={selectPalletFillingValue}
                                options={selectPalletFillingOptions}
                                onChange={onChangePalletFilling}
                                isDisabled={!physicalNumberExistingPallet}
                            />
                        </div>
                        <div className={s.Pallet__fullItem}>
                            <span>Notes</span>
                            <Components.Input
                                type={'text'}
                                value={notes}
                                placeholder={'Notes'}
                                name={'Notes'}
                                autoComplete={'off'}
                                onChange={e => setNotes(e.target.value)}
                            />
                        </div>
                    </div>
                </div>
            </Components.Modal>
        )
    }
)
